import { useSelector } from 'react-redux'

import { checkEligibility } from '@/api/enterprise'
import { selectDomainUsers } from '@/store/features/domain/domainSlice'
import { selectHasActiveOrTrialingEnterpriseSubscription } from '@/store/features/user/userSlice'

/** Centralize the logic for determining if a new member can join the team and what warnings to show when adding or inviting them. */
export const useNewMemberEligibility = () => {
  const hasActiveOrTrialingEnterpriseSubscription = useSelector(selectHasActiveOrTrialingEnterpriseSubscription)
  const domainUsers = useSelector(selectDomainUsers)

  /** Use an email to get the eligibility of a new member */
  const getNewMemberEligibility = async (email: string | null) => {
    if (!email) return false

    const domainUser = domainUsers?.find((user) => user.email.toLowerCase() === email.toLowerCase())

    let eligibility: string | null = null
    const eligibilityResponse = await checkEligibility(email)
    eligibility = eligibilityResponse.data.status

    const domainUserExistsAndUsedTeamTrial =
      domainUser && !domainUser.hasIndividualSubscription && domainUser.teamTrialGiven
    const domainUserExistsAndWillPay = domainUser && domainUser.hasIndividualSubscription

    const willBecomePayer =
      !hasActiveOrTrialingEnterpriseSubscription &&
      (domainUserExistsAndWillPay || eligibility === 'has_individual_subscription')
    const teamNeedsBillingInfo =
      !hasActiveOrTrialingEnterpriseSubscription &&
      (domainUserExistsAndUsedTeamTrial ||
        eligibility === 'no_trial_available' ||
        eligibility === 'has_mobile_subscription')
    const warnEnterprise =
      hasActiveOrTrialingEnterpriseSubscription &&
      (domainUserExistsAndUsedTeamTrial ||
        eligibility === 'no_trial_available' ||
        eligibility === 'has_mobile_subscription')

    // It's important that willBecomePayer is checked before the teamNeedsBillingInfo check
    // because a user can be pro and have used their team trial
    if (willBecomePayer) return 'willBecomePayer'
    if (teamNeedsBillingInfo) return 'teamNeedsBillingInfo'
    if (warnEnterprise) return 'warnEnterprise'
    if (eligibility === 'already_in_enterprise') return 'alreadyInEnterprise'
    if (eligibility === 'no_trial_available') return 'noTrialAvailable'

    // Finally, none of the above was returned so they are eligible to join the team
    // This includes eligibility statuses 'unregistered' and 'eligible_for_team_trial'
    return true
  }

  return {
    getNewMemberEligibility,
  }
}
